<template>
  <div class="grow">
    <div v-if="allocation">
      <h2>
        {{ t('partnerRentOverview') }}
      </h2>

      <PartnerRentInfo :allocation="allocation" />

      <Divider />

      <VehicleAndTimes :booking-or-allocation="allocation" :car="allocation.car" hide-locations />

      <Divider />

      <div class="flex justify-end">
        <ButtonNext
          v-if="allocation"
          :is-loading="isPending"
          variant="default"
          @click.prevent="handleNext"
        >
          {{ nextButtonText }}
        </ButtonNext>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import VehicleAndTimes from '@/components/booking-forms/VehicleAndTimes.vue';
import ButtonNext from '@/components/buttons/ButtonNext.vue';
import Divider from '@/components/Divider.vue';
import { useI18n } from 'vue-i18n';
import { computed, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PartnerRentInfo from './PartnerRentInfo.vue';
import { useUiStore } from '@/stores/ui.store';
import { useAllocationOfTypeOrReroute, useUpdateAllocation } from '@/queries/use-allocations.ts';
import { AllocationStatus, AllocationType } from '@carvia/ros-client-types';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();

const { data: allocation } = useAllocationOfTypeOrReroute(
  route.params.id as string,
  true,
  AllocationType.PARTNER_BOOKING,
  'upcomingHandovers',
);
const { mutateAsync: updateAllocation, isPending } = useUpdateAllocation();

watchEffect(() => {
  if (allocation.value) {
    const { rentalPartner, status, car } = allocation.value;
    uiStore.setHeaderTitle(
      status === AllocationStatus.CONFIRMED ? t('partnerRentHandover') : t('partnerRentReturn'),
      t(`rentalPartners.${rentalPartner}`),
      `${car.vehicleType?.make.name} ${car.vehicleType?.model}`,
    );
  } else {
    uiStore.setHeaderTitle(t('partnerRent'));
  }
});

const nextButtonText = computed(() =>
  allocation.value?.status === AllocationStatus.CONFIRMED
    ? t('confirmHandover')
    : t('confirmReturn'),
);

const handleNext = async () => {
  if (!allocation.value) return;
  const isConfirmed = allocation.value.status === AllocationStatus.CONFIRMED;
  await updateAllocation({
    id: allocation.value.id,
    allocation: {
      status: isConfirmed ? AllocationStatus.HANDED_OVER : AllocationStatus.RETURNED,
    },
  });
  await router.push({
    name: 'partnerRentHandoverDone',
    params: { handedOverOrReturned: isConfirmed ? 'handed-over' : 'returned' },
  });
};
</script>

<i18n lang="json">
{
  "en": {
    "partnerRentHandover": "Partner Rent Handover",
    "partnerRentReturn": "Partner Rent Return",
    "partnerRentOverview": "Partner Rent Overview",
    "confirmHandover": "Confirm car has been handed over",
    "confirmReturn": "Confirm car has been returned"
  },
  "de": {
    "partnerRentHandover": "Partnermiete Übergabe",
    "partnerRentReturn": "Partnermiete Rückgabe",
    "partnerRentOverview": "Partnermiete Übersicht",
    "confirmHandover": "Erfolgte Übergabe bestätigen",
    "confirmReturn": "Erfolgte Rückgabe bestätigen"
  },
  "es": {
    "partnerRentHandover": "Entrega alquiler partner",
    "partnerRentReturn": "Devolución alquiler partner",
    "partnerRentOverview": "Vista general alquiler partner",
    "confirmHandover": "Confirmar que el vehículo ha sido entregado",
    "confirmReturn": "Confirmar que el vehículo ha sido devuelto"
  }
}
</i18n>
